import { useEffect, useState, useRef } from "react";
import { gsap } from "../../lib/gsap";
import Container from "../layout/Container";
import Section from "../layout/Section";

import hero1 from "../../assets/images/hero/hero-1.png";
import hero2 from "../../assets/images/hero/hero-2.png";
import hero3 from "../../assets/images/hero/hero-3.png";
import hero4 from "../../assets/images/hero/hero-4.png";
import hero5 from "../../assets/images/hero/hero-5.png";

const images = [hero1, hero2, hero3, hero4, hero5];

export default function Hero() {
  const [index, setIndex] = useState(0);

  const titleRef = useRef(null);
  const descRef = useRef(null);
  const btnRef = useRef(null);

  /* ================= IMAGE SLIDER ================= */

  useEffect(() => {
    const interval = setInterval(() => {
      setIndex((prev) => (prev + 1) % images.length);
    }, 6000);

    return () => clearInterval(interval);
  }, []);

  /* ================= ENTRANCE ANIMATION ================= */

  useEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline();

      tl.fromTo(
        titleRef.current,
        { y: 60, opacity: 0 },
        { y: 0, opacity: 1, duration: 1, ease: "power3.out" }
      )
        .fromTo(
          descRef.current,
          { y: 40, opacity: 0 },
          { y: 0, opacity: 1, duration: 1, ease: "power3.out" },
          "-=0.6"
        )
        .fromTo(
          btnRef.current,
          { y: 30, opacity: 0 },
          { y: 0, opacity: 1, duration: 1, ease: "power3.out" },
          "-=0.6"
        );
    });

    return () => ctx.revert();
  }, []);

  return (
    <Section
      id="overview"
      className="relative min-h-screen flex items-center overflow-hidden bg-black"
    >
      {/* ================= SLIDES ================= */}

      {images.map((img, i) => (
        <div
          key={i}
          className={`absolute inset-0 transition-opacity duration-1000 ${
            i === index ? "opacity-100" : "opacity-0"
          }`}
        >
          <img
            src={img}
            alt=""
            loading="eager"
            className={`w-full h-full object-cover transition-transform duration-[8000ms] ${
              i === index ? "scale-105 md:scale-110" : "scale-100"
            }`}
          />
        </div>
      ))}

      {/* Overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/70 to-black/90" />

      {/* ================= CONTENT ================= */}

      <Container>
        <div className="relative z-10 max-w-4xl text-white text-center md:text-left">

          <h1
            ref={titleRef}
            className="text-3xl sm:text-4xl md:text-6xl lg:text-7xl font-semibold leading-tight mb-6 md:mb-8"
          >
            Autonomous Systems
            <br className="hidden md:block" />
            <span className="text-[color:var(--color-accent)] block md:inline">
              Engineered for Reality
            </span>
          </h1>

          <p
            ref={descRef}
            className="text-base sm:text-lg md:text-xl text-slate-300 leading-relaxed mb-8 md:mb-10 max-w-xl mx-auto md:mx-0"
          >
            We develop real-time perception, predictive intelligence,
            and adaptive control systems designed to operate safely
            across complex urban and high-speed environments.
          </p>

          <div
            ref={btnRef}
            className="flex flex-col sm:flex-row items-center md:items-start gap-4 sm:gap-6 justify-center md:justify-start"
          >
            <a
              href="#autonomy"
              className="w-full sm:w-auto text-center px-6 py-3 md:px-8 md:py-4 bg-[color:var(--color-accent)] rounded-full text-white hover:opacity-90 transition-all duration-300"
            >
              Explore Technology
            </a>

            <a
              href="#contact"
              className="w-full sm:w-auto text-center px-6 py-3 md:px-8 md:py-4 border border-white/40 rounded-full hover:bg-white hover:text-black transition-all duration-300"
            >
              Contact Us
            </a>
          </div>

        </div>
      </Container>
    </Section>
  );
}